import { useLazyQuery } from '@apollo/client';
import { Button, Form, Input, theme } from 'antd';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { StaffCaseSearchContainerCaseSearchDocument, StaffCaseSearchContainerCaseSearchResultFieldsFragment } from '../../../../generated';
import { ComponentQueryLoader } from '../../../shared/component-query-loader';
import { useSearchState } from '../../../shared/use-search-state';
import { CaseAdvanceSearch } from './case-advance-search';
import { CaseSearchTable } from './case-search-table';

interface CaseSearchContainerProps {
  setFilterDataCount: React.Dispatch<React.SetStateAction<StaffCaseSearchContainerCaseSearchResultFieldsFragment>>;
  isMobile: boolean;
  refreshQuery: boolean;
  setRefreshQuery: React.Dispatch<React.SetStateAction<boolean>>;
}

const CaseSearchContainer: React.FC<CaseSearchContainerProps> = (props) => {
  const [_sortValue, setSortValue] = useState<string[]>([]);

  const [searchResult, setSearchResult] = useState<StaffCaseSearchContainerCaseSearchResultFieldsFragment>();

  const [searchParams] = useSearchParams();

  const { advanceSearch, setAdvanceSearch, inputValue, setInputValue, handleSearchSubmit } = useSearchState();

  const getEverythingFromUrl = (includeParams: string[] = []) => {
    const filters: any = {};
    for (let [key, value] of searchParams.entries()) {
      if (!includeParams.includes(key)) {
        continue;
      }
      filters[key] = value.split(',');
    }
    return filters;
  };

  const getSingleValueFromUrl = (key: string) => {
    return searchParams.get(key);
  };
  const getSearchString = () => {
    if (searchParams.get('search')) {
      return searchParams.get('search');
    } else if (searchParams.get('advanceSearch')) {
      const advancedSearchOptions = searchParams.getAll('filter');
      const advancedSearchValues = searchParams.getAll('value');
      const advancedSearchOperators = searchParams.getAll('operator');
      const queryParts: string[] = [];
      let currentGroup: string[] = [];

      advancedSearchOptions.forEach((filter, index) => {
        if (filter && advancedSearchValues[index]) {
          const filterQuery = `${filter}:(${advancedSearchValues[index]})`;
          currentGroup.push(filterQuery);
          if (index === advancedSearchOptions.length - 1 || advancedSearchOperators[index] === 'OR') {
            queryParts.push(currentGroup.length > 1 ? `(${currentGroup.join(' AND ')})` : currentGroup[0]);
            currentGroup = [];
          }
        }
      });

      const constructedSearchString = queryParts.join(' OR ');
      return constructedSearchString;
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (props.refreshQuery) {
      refetchSearchResult();
      props.setRefreshQuery(false);
    }
  }, [props.refreshQuery]);

  useEffect(() => {
    const executeSearch = async () => {
      await doSearch({
        variables: {
          caseSearchInput2: {
            options: {
              filter: getEverythingFromUrl([
                'state',
                'caseWorkerAssigned',
                'decisionResult',
                'caseType',
                'updatedAt',
                'createdAt',
                'decisionCompletedAt',
                'expirationEligibleAt',
                'lastActivityCreatedAt',
                'revisionRequestRequestedAt',
                'revisionRequestSubmittedAt',
                'submittedAt',
                'decisionCompletedBy',
                'revisionRequestRequestedBy',
                'clientId',
                'credentialType',
                'schemaVersion',
                'tags'
              ]),
              skip: getSingleValueFromUrl('page') ? (parseInt(getSingleValueFromUrl('page') ?? '1', 10) - 1) * parseInt(getSingleValueFromUrl('pageSize') ?? '10', 10) : 0,
              top: parseInt(getSingleValueFromUrl('pageSize') ?? '10', 10),
              orderBy: [`${getSingleValueFromUrl('sortBy') ?? 'updatedAt'} ${getSingleValueFromUrl('orderBy') ?? 'desc'}`]
            },
            searchString: getSearchString()
          }
        }
      }).then((res) => {
        setSearchResult(res.data?.caseSearch as StaffCaseSearchContainerCaseSearchResultFieldsFragment);
      });
    };

    executeSearch();
  }, [searchParams]);

  const [doSearch, { loading: searchResultLoading, error: searchResultError, refetch: refetchSearchResult }] = useLazyQuery(StaffCaseSearchContainerCaseSearchDocument, {
    fetchPolicy: 'network-only'
  });

  const {
    token: { colorBgContainer }
  } = theme.useToken();

  useEffect(() => {
    if (searchResult) {
      props?.setFilterDataCount(searchResult?.facets as StaffCaseSearchContainerCaseSearchResultFieldsFragment);
    }
  }, [searchResult, props?.setFilterDataCount]);

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const onSearchStringClear = () => {
    setInputValue('');
    handleSearchSubmit('');
  };

  const handleOnCloseAdvanceSearch = () => {
    setAdvanceSearch(false);
  };

  return (
    <div
      className={`
      ${props.isMobile ? 'pl-0' : 'pl-12'} 
      bg-[${colorBgContainer}] min-w-[300px] w-full  p-12 pt-0 rounded-lg flex-[1_1_30%]
    `}
    >
      {!advanceSearch ? (
        <Form layout="inline" onFinish={() => handleSearchSubmit(inputValue)} className="mb-1">
          <Form.Item>
            <Input
              onClear={onSearchStringClear}
              allowClear
              maxLength={200}
              value={inputValue}
              onChange={(e) => handleInputChange(e.target.value)}
              className="min-w-80 md:w-full border border-black mb-2"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" className="ml-0 mr-5">
              Search
            </Button>
            <Button
              // className="ml-5"
              onClick={() => {
                setAdvanceSearch(!advanceSearch);
              }}
            >
              Advanced Search
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <CaseAdvanceSearch onClose={handleOnCloseAdvanceSearch} />
      )}
      <ComponentQueryLoader
        error={searchResultError}
        loading={searchResultLoading}
        hasData={searchResult}
        // loadingRows={10}
        hasDataComponent={
          <CaseSearchTable
            sortValue={setSortValue}
            resultCount={searchResult?.count ?? 0}
            data={searchResult?.caseResults as StaffCaseSearchContainerCaseSearchResultFieldsFragment[]}
          />
        }
      />
    </div>
  );
};

export default CaseSearchContainer;
